import {
  EntitiesQueryKeys,
  getEntities,
  getEntitiesList,
  getEntityDetails,
  getEntityDropdown,
  getEntityLabelDropdown,
  getFrequentEntities,
  myEntitiesQueryOptions,
} from "@/api/entities/entities.queries";
import type { EntityListQueryOptions } from "@/api/entities/entities.queries.types";
import type { TSearchParams } from "@/components/ui/Chips/FilterChip/types";
import { useActiveSession } from "@/hooks/useActiveSession";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

export const useEntitiesList = () =>
  useQuery({
    queryKey: [EntitiesQueryKeys.list],
    queryFn: () => getEntities(),
  });

export const useEntitiesListWithFilters = (options: EntityListQueryOptions) => {
  // Include filter in query key to ensure refetch when filter changes
  return useQuery({
    queryKey: [
      EntitiesQueryKeys.list,
      options.page,
      options.pageSize,
      options.search,
      options.sortBy,
      options.sortOrder,
      options.filters,
      options.filterBy,
      options.filterValue,
    ],
    queryFn: ({ signal }) => getEntitiesList(options, signal),
    placeholderData: keepPreviousData,
  });
};

export const useEntityDropdown = (type: string, search?: string) => {
  return useQuery({
    queryKey: [EntitiesQueryKeys.dropdown, type, search],
    queryFn: ({ signal }) => getEntityDropdown(type, search, signal),
    placeholderData: keepPreviousData,
    enabled: !!type,
  });
};

export const useEntityFilteredData = ({
  searchTerm,
  searchParams,
  enabled = true,
}: {
  searchTerm: string;
  searchParams?: TSearchParams;
  label: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [EntitiesQueryKeys.dropdown, searchParams?.type, searchTerm],
    queryFn: ({ signal }) => {
      // For entity dropdown, we use type=entity and search
      const entityType = searchParams?.type || "entity";
      // Pass search term only if it's not empty, otherwise fetch all
      return getEntityDropdown(entityType, searchTerm || undefined, signal);
    },
    placeholderData: keepPreviousData,
    enabled: !!searchParams && searchParams.type === "entity" && enabled,
  });
};

export const useEntityLabelDropdown = (enabled = true, search = "") => {
  return useQuery({
    queryKey: [EntitiesQueryKeys.entityLabelDropdown, search],
    queryFn: ({ signal }) => getEntityLabelDropdown({ search }, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useFrequentEntities = (enabled = true) => {
  return useQuery({
    queryKey: [EntitiesQueryKeys.frequentEntities],
    queryFn: ({ signal }) => getFrequentEntities(signal),
    // The only writer is the visit mutation, which invalidates this key, so a
    // background refetch on every open would buy nothing.
    staleTime: Infinity,
    enabled,
  });
};

export const useMyEntities = (enabled = true) => {
  const { userId } = useActiveSession();
  return useQuery({
    ...myEntitiesQueryOptions(userId),
    enabled: enabled && !!userId,
  });
};

export const useEntityData = (entityId: string) => {
  return useQuery({
    queryKey: [EntitiesQueryKeys.details, entityId],
    queryFn: () => getEntityDetails(entityId),
    enabled: !!entityId,
  });
};
